import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import BotanicalBackdrop, { LeafGlyph } from "@/components/BotanicalBackdrop";
import { BrandLogo } from "@/components/BrandMark";
import usePageTitle from "@/hooks/usePageTitle";

/* ================================================================== */
/*  Motion primitives                                                  */
/* ================================================================== */

const useReveal = () => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, shown];
};

/* Word-by-word entrance */
const SplitHeadline = ({ children, className = "", testId, delayBase = 0 }) => {
  const [ref, shown] = useReveal();
  const text = String(children);
  return (
    <h2 ref={ref} className={className} data-testid={testId} aria-label={text}>
      {text.split(" ").map((word, i) => (
        <span
          key={i}
          className="inline-block overflow-hidden align-baseline mr-[0.24em]"
          /* Bottom padding + negative margin: creates a safe zone for
             italic descenders (g, y, p, q, j) inside the mask without
             affecting surrounding layout. */
          style={{ paddingBottom: "0.22em", marginBottom: "-0.22em" }}
        >
          <span
            className="inline-block will-change-transform"
            style={{
              transform: shown ? "translateY(0)" : "translateY(110%)",
              opacity: shown ? 1 : 0,
              transition: `transform 1000ms cubic-bezier(0.2,0.7,0.15,1) ${delayBase + i * 70}ms, opacity 700ms ease ${delayBase + i * 70}ms`,
            }}
          >
            {word}
          </span>
        </span>
      ))}
    </h2>
  );
};

const Reveal = ({ children, delay = 0, className = "", as: Tag = "div", testId }) => {
  const [ref, shown] = useReveal();
  return (
    <Tag
      ref={ref}
      data-testid={testId}
      className={className}
      style={{
        transform: shown ? "translateY(0)" : "translateY(28px)",
        opacity: shown ? 1 : 0,
        transition: `transform 900ms cubic-bezier(0.2,0.7,0.15,1) ${delay}ms, opacity 900ms ease ${delay}ms`,
      }}
    >
      {children}
    </Tag>
  );
};

/* rAF-throttled parallax */
const useParallax = (strength = 0.15) => {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const progress = 1 - (rect.top + rect.height / 2) / (vh + rect.height / 2);
      setOffset(progress * strength * 100);
      raf = 0;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", update);
    };
  }, [strength]);
  return [ref, offset];
};

/* Grain overlay used on dark cinematic sections */
const Grain = () => (
  <div
    aria-hidden="true"
    className="absolute inset-0 opacity-[0.18] mix-blend-overlay pointer-events-none"
    style={{
      backgroundImage:
        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.9  0 0 0 0 0.8  0 0 0 0 0.6  0 0 0 0.28 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
    }}
  />
);

/* Chapter number marker */
const ChapterMark = ({ n, label, dark = false }) => (
  <div className="flex items-center gap-4">
    <div className={"serif italic text-[15px] " + (dark ? "text-[var(--hk-gold)]" : "text-[var(--hk-gold-deep)]")}>{n}</div>
    <div className={"w-10 h-px " + (dark ? "bg-[var(--hk-gold)]/50" : "bg-[var(--hk-border)]")} />
    <div className={"text-[11px] tracking-[0.42em] uppercase " + (dark ? "text-[var(--hk-gold)]" : "text-[var(--hk-gold-deep)]")}>{label}</div>
  </div>
);

/* ================================================================== */
/*  Page                                                               */
/* ================================================================== */

const OurStoryPage = () => {
  usePageTitle("Our Story · Founder's Manifesto");
  const [heroRef, heroOff] = useParallax(0.3);
  const [discoveryRef, discoveryOff] = useParallax(0.35);
  const [turningRef, turningOff] = useParallax(0.25);
  const [dreamRef, dreamOff] = useParallax(0.2);

  return (
    <div data-testid="our-story-cinematic">

      {/* ============================================================ */}
      {/* OPENING — a personal declaration                              */}
      {/* ============================================================ */}
      <section
        ref={heroRef}
        className="relative min-h-[94vh] flex items-center overflow-hidden bg-[var(--hk-ink)] text-[#EDE4CE]"
        data-testid="story-opening"
      >
        {/* atmospheric radial glows */}
        <div
          aria-hidden="true"
          className="absolute -top-40 -left-40 w-[720px] h-[720px] rounded-full blur-[140px] opacity-40 pointer-events-none"
          style={{
            background: "radial-gradient(closest-side, rgba(217,117,53,0.55), transparent 70%)",
            transform: `translate3d(0, ${heroOff * 0.6}px, 0)`,
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-56 -right-40 w-[720px] h-[720px] rounded-full blur-[140px] opacity-30 pointer-events-none"
          style={{
            background: "radial-gradient(closest-side, rgba(201,169,97,0.55), transparent 70%)",
            transform: `translate3d(0, ${-heroOff * 0.4}px, 0)`,
          }}
        />
        {/* Devanagari watermark */}
        <div
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-end pr-4 md:pr-16 pointer-events-none select-none"
          style={{ transform: `translate3d(0, ${heroOff * 0.5}px, 0)` }}
        >
          <div
            className="serif text-[280px] md:text-[420px] lg:text-[560px] leading-none tracking-tight opacity-[0.06]"
            style={{ color: "#EDE4CE" }}
            lang="hi"
          >
            आ
          </div>
        </div>
        <Grain />

        <div className="relative mx-auto max-w-6xl px-6 lg:px-10 w-full">
          <Reveal delay={100}>
            <ChapterMark n="I." label="An Honest Beginning" dark />
          </Reveal>

          <SplitHeadline
            className="serif mt-10 text-[52px] sm:text-[80px] lg:text-[120px] leading-[1.08] tracking-[-0.02em] text-white font-medium"
            testId="story-hero-line-1"
            delayBase={280}
          >
            I was not building
          </SplitHeadline>
          <SplitHeadline
            className="serif mt-2 text-[52px] sm:text-[80px] lg:text-[120px] leading-[1.08] tracking-[-0.02em] text-white font-medium"
            testId="story-hero-line-2"
            delayBase={520}
          >
            a brand.
          </SplitHeadline>
          <SplitHeadline
            className="serif mt-3 text-[52px] sm:text-[80px] lg:text-[120px] leading-[1.08] tracking-[-0.02em] italic text-[var(--hk-orange)] font-medium"
            testId="story-hero-line-3"
            delayBase={900}
          >
            I was rediscovering something.
          </SplitHeadline>

          <Reveal delay={1600} className="mt-16 max-w-xl">
            <p className="text-[15px] sm:text-[16px] leading-[1.95] text-white/70 font-light">
              This is not a company story. It is the account of a personal
              discovery — one that eventually asked to become something more.
            </p>
            <div className="mt-8 flex items-center gap-3 text-[11px] tracking-[0.42em] uppercase text-[var(--hk-gold)]/80">
              <span>A note from the founder</span>
            </div>
          </Reveal>

          <Reveal delay={1900} className="mt-14 flex items-center gap-3 text-[11px] tracking-[0.36em] uppercase text-[var(--hk-gold)]/70">
            <span>Read</span>
            <ArrowDown size={14} className="animate-bounce" />
          </Reveal>
        </div>
      </section>

      {/* ============================================================ */}
      {/* CHAPTER II — THE CLASSROOM TRUTH                              */}
      {/* ============================================================ */}
      <section className="relative py-32 lg:py-48 overflow-hidden" data-testid="story-classroom">
        <BotanicalBackdrop variant="sideLeft" />
        <div className="relative mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <ChapterMark n="II." label="The Classroom Truth" />
          </Reveal>

          <SplitHeadline
            className="serif mt-10 text-[40px] sm:text-[60px] lg:text-[80px] leading-[1.08] tracking-tight text-[var(--hk-ink)]"
            testId="story-classroom-headline"
          >
            For years, I taught.
          </SplitHeadline>

          <div className="mt-16 grid md:grid-cols-12 gap-10 items-start">
            <Reveal delay={100} className="md:col-span-5">
              <div className="serif italic text-[26px] sm:text-[30px] leading-[1.35] text-[var(--hk-ink)]">
                Long before HerbKranti,
                <br />
                I spent years in classrooms —
                <br />
                first as a student, then as a teacher.
              </div>
            </Reveal>
            <Reveal delay={220} className="md:col-span-7 md:pl-8 md:border-l md:border-[var(--hk-border)]">
              <p className="text-[17px] leading-[2] text-[var(--hk-ink-2)]">
                Teaching gave me many things. But it gave me one truth I
                could never quite put down:
              </p>
              <div className="mt-8 pl-6 border-l-2 border-[var(--hk-orange)]">
                <p className="serif text-[22px] sm:text-[26px] leading-[1.4] text-[var(--hk-ink)]">
                  Knowledge changes decisions.
                  <br />
                  Better decisions change lives.
                </p>
              </div>
              <p className="mt-8 text-[17px] leading-[2] text-[var(--hk-ink-2)]">
                That single sentence stayed with me long after I had
                stepped away from the classroom. It became the quiet lens
                through which I saw everything I studied next.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* CHAPTER III — THE SEARCH (not for opportunity)                */}
      {/* ============================================================ */}
      <section className="relative py-32 lg:py-44 overflow-hidden bg-[var(--hk-warm-ivory)]" data-testid="story-search">
        <div className="relative mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <ChapterMark n="III." label="The Search" />
          </Reveal>

          <SplitHeadline
            className="serif mt-10 text-[38px] sm:text-[58px] lg:text-[72px] leading-[1.08] tracking-tight text-[var(--hk-ink)]"
            testId="story-search-headline"
          >
            I was not searching for a business.
          </SplitHeadline>
          <SplitHeadline
            className="serif mt-3 text-[38px] sm:text-[58px] lg:text-[72px] leading-[1.08] tracking-tight italic text-[var(--hk-orange-deep)]"
            testId="story-search-headline-2"
            delayBase={200}
          >
            I was searching for understanding.
          </SplitHeadline>

          <div className="mt-16 max-w-2xl">
            <Reveal delay={200}>
              <p className="text-[18px] leading-[2] text-[var(--hk-ink-2)]">
                When I first began reading about health and wellness, I was
                not looking for a market to enter. I was looking for
                answers — for myself, and for the people I quietly worried
                about.
              </p>
            </Reveal>
            <Reveal delay={340}>
              <p className="mt-6 text-[18px] leading-[2] text-[var(--hk-ink-2)]">
                And the more I studied, the more one system kept returning
                to the centre of every honest answer I found. Ayurveda.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* CHAPTER IV — THE DISCOVERY (centrepiece)                      */}
      {/* ============================================================ */}
      <section
        ref={discoveryRef}
        className="relative py-40 lg:py-56 overflow-hidden bg-[var(--hk-ink)] text-[#EDE4CE]"
        data-testid="story-discovery"
      >
        <div
          aria-hidden="true"
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full blur-[160px] opacity-25 pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(217,117,53,0.6), transparent 70%)" }}
        />
        {/* Massive faint outlined word behind */}
        <div
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
          style={{ transform: `translate3d(0, ${discoveryOff * 1.1}px, 0)` }}
        >
          <div
            className="serif text-[220px] sm:text-[340px] lg:text-[480px] leading-none tracking-[-0.04em] font-medium"
            style={{
              color: "transparent",
              WebkitTextStroke: "1px rgba(201, 169, 97, 0.28)",
            }}
          >
            AYURVEDA
          </div>
        </div>
        <Grain />

        <div className="relative mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <ChapterMark n="IV." label="The Discovery" dark />
          </Reveal>

          <SplitHeadline
            className="serif mt-10 text-[46px] sm:text-[72px] lg:text-[104px] leading-[1.08] tracking-[-0.02em] text-white font-medium"
            testId="story-discovery-headline"
          >
            Ayurveda had never disappeared.
          </SplitHeadline>
          <SplitHeadline
            className="serif mt-3 text-[46px] sm:text-[72px] lg:text-[104px] leading-[1.08] tracking-[-0.02em] italic text-[var(--hk-orange)] font-medium"
            testId="story-discovery-headline-2"
            delayBase={280}
          >
            We had only forgotten how to see it.
          </SplitHeadline>

          <Reveal delay={900} className="mt-20 max-w-3xl">
            <p className="text-[19px] leading-[2] text-white/80">
              This was the moment everything changed for me. What I had
              expected to find was a system that had faded, become outdated,
              lost its relevance in modern life.
            </p>
            <p className="mt-6 text-[19px] leading-[2] text-white/80">
              Instead I found something else entirely.
            </p>
          </Reveal>

          <Reveal delay={1050} className="mt-14">
            <div className="serif text-[28px] sm:text-[38px] lg:text-[48px] leading-[1.25] text-white max-w-3xl">
              It was not outdated.
              <br />
              <span className="italic text-[var(--hk-orange)]">It had been misunderstood.</span>
            </div>
          </Reveal>

          <Reveal delay={1250} className="mt-14 max-w-2xl">
            <p className="text-[17px] leading-[2] text-white/70">
              An entire generation had drifted from Ayurveda not because
              the wisdom had failed — but because no one had translated it
              for the life they were living now. The knowledge remained.
              The bridge to it did not.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============================================================ */}
      {/* CHAPTER V — THE TURNING POINT (the headline)                  */}
      {/* ============================================================ */}
      <section
        ref={turningRef}
        className="relative py-48 lg:py-64 overflow-hidden"
        data-testid="story-turning-point"
      >
        <div
          aria-hidden="true"
          className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full blur-[140px] opacity-40 pointer-events-none"
          style={{
            background: "radial-gradient(closest-side, rgba(217,117,53,0.45), transparent 70%)",
            transform: `translate3d(0, ${turningOff * 0.6}px, 0)`,
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-40 -right-40 w-[620px] h-[620px] rounded-full blur-[160px] opacity-35 pointer-events-none"
          style={{
            background: "radial-gradient(closest-side, rgba(201,169,97,0.5), transparent 70%)",
            transform: `translate3d(0, ${-turningOff * 0.5}px, 0)`,
          }}
        />

        <div className="relative mx-auto max-w-6xl px-6 lg:px-10 text-center">
          <Reveal>
            <div className="mx-auto flex items-center justify-center gap-4">
              <div className="w-16 h-px bg-[var(--hk-gold)]" />
              <div className="text-[11px] tracking-[0.42em] uppercase text-[var(--hk-gold-deep)]">The Turning Point</div>
              <div className="w-16 h-px bg-[var(--hk-gold)]" />
            </div>
          </Reveal>

          <SplitHeadline
            className="serif mt-12 text-[48px] sm:text-[80px] lg:text-[120px] leading-[1.08] tracking-[-0.02em] text-[var(--hk-ink)] font-medium"
            testId="story-turning-headline-1"
          >
            Before I Built HerbKranti,
          </SplitHeadline>
          <SplitHeadline
            className="serif mt-3 text-[48px] sm:text-[80px] lg:text-[120px] leading-[1.08] tracking-[-0.02em] italic text-[var(--hk-orange-deep)] font-medium"
            testId="story-turning-headline-2"
            delayBase={320}
          >
            I Rediscovered Ayurveda.
          </SplitHeadline>

          <Reveal delay={1100} className="mt-16 max-w-2xl mx-auto">
            <p className="text-[18px] leading-[2] text-[var(--hk-ink-2)]">
              And once I had — I could not unsee what needed to be built.
              Not another supplement company. Not another shelf of jars.
              Something quieter, and something braver.
            </p>
          </Reveal>

          <Reveal delay={1300} className="mt-10">
            <div className="hk-divider mx-auto max-w-md"><LeafGlyph className="w-4 h-4" /></div>
          </Reveal>
        </div>
      </section>

      {/* ============================================================ */}
      {/* CHAPTER VI — THE BRIDGE                                       */}
      {/* ============================================================ */}
      <section className="relative py-32 lg:py-44 overflow-hidden bg-[var(--hk-warm-ivory)]" data-testid="story-bridge">
        <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5 md:sticky md:top-32">
              <Reveal>
                <ChapterMark n="V." label="What HerbKranti Is" />
              </Reveal>
              <Reveal delay={120}>
                <h2 className="serif mt-8 text-[44px] sm:text-[58px] lg:text-[76px] leading-[1.08] tracking-tight text-[var(--hk-ink)]">
                  Not a company.
                  <br />
                  <span className="italic-accent text-[var(--hk-orange-deep)]">A bridge.</span>
                </h2>
              </Reveal>
              <Reveal delay={280}>
                <p className="mt-8 text-[17px] leading-[1.95] text-[var(--hk-ink-2)] max-w-md">
                  HerbKranti was never designed to be another supplement
                  business. It was designed to be a bridge — a careful,
                  modern, honest passage between what was known, and what
                  needs to be lived today.
                </p>
              </Reveal>
            </div>

            <div className="md:col-span-7 space-y-6">
              {[
                { l: "Ancient Wisdom", r: "Modern Living" },
                { l: "Nature", r: "Everyday Life" },
                { l: "Traditional Knowledge", r: "Modern Presentation" },
                { l: "Trust", r: "Wellness" },
              ].map((row, i) => (
                <Reveal key={i} delay={i * 90}>
                  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 md:gap-8 py-6 border-b border-[var(--hk-border)] last:border-b-0">
                    <div className="serif text-right text-[22px] sm:text-[28px] lg:text-[34px] leading-tight text-[var(--hk-ink)]">
                      {row.l}
                    </div>
                    <div className="text-[var(--hk-orange-deep)] text-[24px] serif leading-none">↔</div>
                    <div className="serif italic text-left text-[22px] sm:text-[28px] lg:text-[34px] leading-tight text-[var(--hk-ink)]">
                      {row.r}
                    </div>
                  </div>
                </Reveal>
              ))}

              <Reveal delay={520} className="pt-8">
                <p className="serif italic text-[22px] sm:text-[26px] leading-[1.4] text-[var(--hk-ink)] max-w-lg">
                  The products are not the destination.
                  <br />
                  They are the <span className="text-[var(--hk-orange-deep)]">outcome</span> of this bridge.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* CHAPTER VII — QUALITY OVER SPEED (dark manifesto)             */}
      {/* ============================================================ */}
      <section className="relative py-40 lg:py-56 overflow-hidden bg-[var(--hk-ink)] text-[#EDE4CE]" data-testid="story-quality">
        <div
          aria-hidden="true"
          className="absolute -top-40 -right-40 w-[720px] h-[720px] rounded-full blur-[140px] opacity-25 pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(217,117,53,0.55), transparent 70%)" }}
        />
        <Grain />

        <div className="relative mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <ChapterMark n="VI." label="The Standard" dark />
          </Reveal>

          <SplitHeadline
            className="serif mt-10 text-[46px] sm:text-[72px] lg:text-[100px] leading-[1.08] tracking-[-0.02em] text-white font-medium"
            testId="story-quality-headline"
          >
            We do not launch products
          </SplitHeadline>
          <SplitHeadline
            className="serif mt-3 text-[46px] sm:text-[72px] lg:text-[100px] leading-[1.08] tracking-[-0.02em] italic text-[var(--hk-orange)] font-medium"
            testId="story-quality-headline-2"
            delayBase={220}
          >
            because the market asks.
          </SplitHeadline>

          <Reveal delay={800} className="mt-16 max-w-2xl">
            <p className="text-[18px] leading-[2] text-white/75">
              A dozen products this year would be easy. That has never been
              what we set out to do. Every product must earn the HerbKranti
              name — quietly, thoroughly, without compromise.
            </p>
          </Reveal>

          <div className="mt-20 grid md:grid-cols-3 gap-6">
            {[
              { t: "Every ingredient must have purpose.", d: "Nothing is included because a formulation sheet requires it. Every constituent is chosen with reason." },
              { t: "Every formulation must be considered.", d: "We do not rush a bottle to a shelf. We refine, we test, we wait until the product earns our own conviction." },
              { t: "Trust is not a claim. It is a habit.", d: "We would rather understate what we know than overstate what we hope. Slowness is the shape of that promise." },
            ].map((c, i) => (
              <Reveal key={i} delay={i * 120}>
                <div className="p-8 rounded-2xl border border-white/12 bg-white/[0.03] backdrop-blur-sm h-full">
                  <div className="serif italic text-[13px] text-[var(--hk-gold)] mb-4">— principle {i + 1}</div>
                  <h3 className="serif text-[22px] leading-snug text-white">{c.t}</h3>
                  <p className="mt-4 text-[15px] leading-[1.85] text-white/70">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={600} className="mt-20">
            <div className="serif italic text-center text-[26px] sm:text-[34px] lg:text-[42px] leading-[1.25] text-white max-w-3xl mx-auto">
              Trust takes time.
              <br />
              <span className="text-[var(--hk-orange)]">Quality takes patience.</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============================================================ */}
      {/* CHAPTER VIII — THE DREAM (global)                             */}
      {/* ============================================================ */}
      <section
        ref={dreamRef}
        className="relative py-32 lg:py-44 overflow-hidden"
        data-testid="story-dream"
      >
        <BotanicalBackdrop variant="corners" />
        <div
          aria-hidden="true"
          className="absolute -top-20 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full blur-[140px] opacity-25 pointer-events-none"
          style={{
            background: "radial-gradient(closest-side, rgba(201,169,97,0.55), transparent 70%)",
            transform: `translate3d(0, ${dreamOff * 0.5}px, 0)`,
          }}
        />

        <div className="relative mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <ChapterMark n="VII." label="The Dream" />
          </Reveal>

          <SplitHeadline
            className="serif mt-10 text-[40px] sm:text-[62px] lg:text-[84px] leading-[1.08] tracking-tight text-[var(--hk-ink)]"
            testId="story-dream-headline"
          >
            The dream was never about products.
          </SplitHeadline>

          <div className="mt-16 grid md:grid-cols-2 gap-12">
            <Reveal delay={100}>
              <p className="text-[18px] leading-[2] text-[var(--hk-ink-2)]">
                The dream is quieter, and larger. To help a generation
                rediscover confidence in Ayurveda. To make Indian botanical
                wisdom feel modern. Premium. Beautiful. Understandable.
                Trustworthy.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <p className="text-[18px] leading-[2] text-[var(--hk-ink-2)]">
                Not only in the homes it once belonged to — but on the
                shelves of the world. From a small corner of India to the
                mornings of anyone, anywhere, who is quietly ready to live
                a more considered life.
              </p>
            </Reveal>
          </div>

          <Reveal delay={400} className="mt-20">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-center">
              {["Modern.", "Premium.", "Beautiful.", "Understandable.", "Trustworthy.", "Relevant."].map((w, i) => (
                <span
                  key={w}
                  className="serif italic text-[22px] sm:text-[30px] lg:text-[36px] text-[var(--hk-ink)]"
                  style={{ opacity: 0.35 + i * 0.11 }}
                >
                  {w}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============================================================ */}
      {/* THE ENDING — full-screen manifesto with signature             */}
      {/* ============================================================ */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden bg-[var(--hk-ink)] text-[#EDE4CE]" data-testid="story-ending">
        <div
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1100px] h-[1100px] rounded-full blur-[200px] opacity-30 pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(217,117,53,0.5), transparent 70%)" }}
        />
        {/* Faint Devanagari watermark */}
        <div
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
        >
          <div
            className="serif text-[260px] md:text-[420px] lg:text-[560px] leading-none tracking-tight opacity-[0.05]"
            style={{ color: "#EDE4CE" }}
            lang="hi"
          >
            क्रान्ति
          </div>
        </div>
        <Grain />

        <div className="relative mx-auto max-w-5xl px-6 lg:px-10 w-full py-24">
          <Reveal>
            <div className="flex items-center gap-4">
              <div className="w-14 h-px bg-[var(--hk-gold)]/60" />
              <div className="text-[11px] tracking-[0.42em] uppercase text-[var(--hk-gold)]">The Promise</div>
            </div>
          </Reveal>

          <SplitHeadline
            className="serif mt-10 text-[42px] sm:text-[64px] lg:text-[92px] leading-[1.08] tracking-[-0.02em] text-white font-medium"
            testId="story-ending-line-1"
          >
            We are not building products.
          </SplitHeadline>
          <SplitHeadline
            className="serif mt-3 text-[42px] sm:text-[64px] lg:text-[92px] leading-[1.08] tracking-[-0.02em] italic text-[var(--hk-orange)] font-medium"
            testId="story-ending-line-2"
            delayBase={260}
          >
            We are rebuilding trust.
          </SplitHeadline>

          <div className="mt-14 space-y-2">
            {[
              "One ingredient.",
              "One formulation.",
              "One family.",
              "One generation at a time.",
            ].map((line, i) => (
              <Reveal key={line} delay={900 + i * 220}>
                <div className="serif text-[28px] sm:text-[40px] lg:text-[54px] leading-[1.1] text-white/90 tracking-tight">
                  {line}
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={2200} className="mt-20 pt-10 border-t border-white/12 max-w-md">
            <div
              className="serif italic text-[36px] sm:text-[44px] text-[var(--hk-orange)]"
              style={{ lineHeight: 1.15, paddingBottom: "0.08em" }}
            >
              — Pankaj D. Agarwal
            </div>
            <div className="mt-4 text-[12px] tracking-[0.32em] uppercase text-white/60">
              Founder &amp; Partner
            </div>
            <div className="text-[12px] tracking-[0.32em] uppercase text-white/60">
              HerbKranti Wellness LLP
            </div>
          </Reveal>

          <Reveal delay={2500} className="mt-16 flex flex-wrap items-center gap-4">
            <Link to="/products" className="hk-btn-primary" data-testid="story-ending-products-btn">
              Begin with Our First Product <ArrowRight size={16} />
            </Link>
            <Link to="/" className="text-[13px] tracking-[0.28em] uppercase text-white/70 hover:text-white transition-colors" data-testid="story-ending-home-link">
              Back to Home
            </Link>
          </Reveal>

          <Reveal delay={2700} className="mt-16 flex items-center gap-3 opacity-80">
            <BrandLogo variant="dark" markSize={44} wordSize={16} showTagline={true} taglineSize={9} testId="story-brand-logo" />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default OurStoryPage;
